import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { TEXT_MUTED, BG_SURFACE } from './legacy-ball/table-management/designTokens';

interface PageInfo {
  id: string;
  slug: string;
  displayName: string;
  alias: string | null;
}

interface ScreenIdentifierBarProps {
  onReportBug?: () => void;
}

/**
 * A small fixed-position indicator (top-right corner) showing the current
 * screen ID and alias. Used to help users identify which screen they are on
 * when reporting bugs. Includes an inline bug-report button.
 */
export function ScreenIdentifierBar({ onReportBug }: ScreenIdentifierBarProps) {
  const location = useLocation();
  const isMobile = useMediaQuery(767);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // Extract full path as display slug, stripping dynamic UUID/ID segments
  const fullSlug = location.pathname
    .split('/')
    .filter(Boolean)
    .filter((seg) => !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg) && !/^[0-9a-f]{24,}$/i.test(seg))
    .join('/') || 'root';

  // Use first segment for PageRegistry lookup (aliases are registered at top-level)
  const registrySlug = location.pathname.split('/').filter(Boolean)[0] || '';

  useEffect(() => {
    if (!registrySlug) {
      setLoading(false);
      return;
    }

    setLoading(true);
    apiClient<PageInfo>(`/page-registry/${registrySlug}`)
      .then((data) => {
        setPageInfo(data);
      })
      .catch(() => {
        // Page not found in registry — show slug only
        setPageInfo(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [registrySlug]);

  // Show nothing while loading
  if (loading) return null;

  // Build display text — show full path, alias from registry
  const alias = pageInfo?.alias;
  const displayText = alias ? `🏷️ ${alias} (${fullSlug})` : `🏷️ ${fullSlug}`;

  return (
    <div style={{ ...styles.container, top: isMobile ? '62px' : '8px' }} aria-label="Current screen identifier">
      {displayText}
      {onReportBug && (
        <button
          style={styles.bugButton}
          onClick={onReportBug}
          aria-label="Report a bug"
          title="Report a bug"
        >
          🐛
        </button>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    top: '8px',
    right: '8px',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    backgroundColor: BG_SURFACE,
    opacity: 0.9,
    padding: '5px 14px',
    borderRadius: '4px',
    zIndex: 201,
    pointerEvents: 'auto',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  bugButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 4px',
    fontSize: '0.85rem',
    lineHeight: 1,
    borderRadius: '3px',
  },
};
